import { state } from './state.js';
import * as UI from './ui.js';
import * as API from './api.js';
import {
    attachLibraryPreviewScroll,
    hasLoadedInitialLibraryPreview,
    loadInitialLibraryPreview,
    resetLibraryPreviewState,
} from './library_preview.js';
import { UI_COPY } from './config/uiCopy.js';
import { refreshSettingsVisibility } from './workflow_selection.js';
import { setLibraryDropzoneState, setLibraryPreviewVisibility, setLibraryWorkspaceVisibility } from './ui/library_workspace.js';
import { openConfirmModal } from './modal_manager.js';
import { showToast } from './ui/toast.js';

let _libraryEventsBound = false;
const _dropPreviewFillInFlight = new Map();
let _dropPreviewResizeBound = false;
const SAMPLE_LIBRARY_ID = 'demo0001';

function _emitCreateDraftDirty(reason = 'library') {
    document.dispatchEvent(new CustomEvent('workflow:draft_dirty', {
        detail: {
            reason: String(reason || 'library'),
            library_id: String(state.selectedLibraryId || '').trim(),
        },
    }));
}

function _ensureSampleLibraryFallback(libraries = [], _isAnonymous = false) {
    const libs = Array.isArray(libraries) ? libraries.slice() : [];
    if (libs.length > 0) return libs;
    return [{
        id: SAMPLE_LIBRARY_ID,
        name: 'Sample Library',
        is_public: true,
        is_system: true,
        is_owner: false,
        tile_count: 0,
        previews: [],
    }];
}

function _requestDropPreviewFill(libraryId, requiredCount) {
    const libId = String(libraryId || '');
    const needed = Math.max(0, Number(requiredCount || 0));
    if (!libId || needed <= 0) return;
    const idx = (state.libraries || []).findIndex((lib) => String(lib.id || '') === libId);
    if (idx < 0) return;
    const current = Array.isArray(state.libraries[idx].previews) ? state.libraries[idx].previews.length : 0;
    const tileCount = Math.max(0, Number(state.libraries[idx].tile_count || 0));
    if (tileCount <= 0) return;
    const targetCount = Math.min(needed, tileCount);
    if (targetCount <= 0 || current >= targetCount) return;

    const inFlightTarget = Number(_dropPreviewFillInFlight.get(libId) || 0);
    if (inFlightTarget >= targetCount) return;
    _dropPreviewFillInFlight.set(libId, targetCount);

    const limit = Math.max(9, Math.min(100, targetCount));
    API.getLibraryPreview(libId, limit, 0)
        .then((data) => {
            const at = (state.libraries || []).findIndex((lib) => String(lib.id || '') === libId);
            if (at < 0) return;
            const images = Array.isArray(data && data.images) ? data.images : [];
            const prev = state.libraries[at] || {};
            const prevPreviews = Array.isArray(prev.previews) ? prev.previews : [];
            const nextTileCount = Number((data && data.count) || prev.tile_count || 0);
            const nextPreviews = images.length ? images : prevPreviews;
            const tileChanged = nextTileCount !== Number(prev.tile_count || 0);
            const previewsChanged =
                nextPreviews.length !== prevPreviews.length ||
                nextPreviews.some((url, i) => url !== prevPreviews[i]);
            if (!tileChanged && !previewsChanged) return;
            const updated = {
                ...prev,
                tile_count: nextTileCount,
                previews: nextPreviews,
            };
            state.libraries[at] = updated;
            if (typeof UI.updateLibraryStripItem === 'function') {
                UI.updateLibraryStripItem(updated);
            }
            document.dispatchEvent(new CustomEvent('library:selected-updated', { detail: { library: updated } }));
        })
        .catch(() => {})
        .finally(() => {
            _dropPreviewFillInFlight.delete(libId);
        });
}

function _applyLibraryDropzoneState() {
    setLibraryDropzoneState({
        libraries: state.libraries,
        selectedLibraryId: state.selectedLibraryId,
        isAnonymous: state.isAnonymous,
        onNeedPreviewFill: _requestDropPreviewFill,
    });
}

function _getSelectedLibrary() {
    const selectedId = String(state.selectedLibraryId || '');
    if (!selectedId) return null;
    return (state.libraries || []).find((lib) => String(lib.id || '') === selectedId) || null;
}

function _syncLibraryPreviewPanel({ collapseOnReveal = false } = {}) {
    const selected = _getSelectedLibrary();
    const tileCount = Math.max(0, Number((selected && selected.tile_count) || 0));
    if (!selected || tileCount <= 0) {
        setLibraryPreviewVisibility(false);
        return;
    }
    const area = document.getElementById('libPreviewArea');
    const wasVisible = Boolean(area && !area.classList.contains('hidden'));
    UI.renderLibraryPreview(
        {
            count: tileCount,
            images: Array.isArray(selected.previews) ? selected.previews : [],
        },
        false,
        { forceCollapsed: Boolean(collapseOnReveal || !wasVisible) },
    );
}

function _bindLibraryEvents() {
    if (_libraryEventsBound) return;
    _libraryEventsBound = true;
    document.addEventListener('library:selected-updated', () => {
        _applyLibraryDropzoneState();
        _syncLibraryPreviewPanel();
        refreshSettingsVisibility();
    });
    document.addEventListener('library:preview-collapsed-changed', (event) => {
        const collapsed = Boolean(event && event.detail && event.detail.collapsed);
        if (!collapsed) {
            _loadSelectedLibraryPreviewIfNeeded().catch(() => {});
        }
    });
    if (!_dropPreviewResizeBound && typeof window !== 'undefined') {
        let rafId = null;
        window.addEventListener('resize', () => {
            if (rafId !== null) {
                if (typeof cancelAnimationFrame === 'function') {
                    cancelAnimationFrame(rafId);
                }
                rafId = null;
            }
            if (typeof requestAnimationFrame === 'function') {
                rafId = requestAnimationFrame(() => {
                    rafId = null;
                    _applyLibraryDropzoneState();
                });
            } else {
                _applyLibraryDropzoneState();
            }
        });
        _dropPreviewResizeBound = true;
    }
}

async function _loadSelectedLibraryPreviewIfNeeded() {
    const selectedId = state.selectedLibraryId;
    if (!selectedId) return;
    if (hasLoadedInitialLibraryPreview(selectedId)) return;
    const selected = _getSelectedLibrary();
    const currentTileCount = Math.max(0, Number((selected && selected.tile_count) || 0));
    const currentPreviews = Array.isArray(selected && selected.previews) ? selected.previews : [];
    if (typeof UI.setLibraryPreviewLoadState === 'function') {
        UI.setLibraryPreviewLoadState({
            tileCount: currentTileCount,
            images: currentPreviews,
            loading: true,
        });
    }
    try {
        const previewData = await loadInitialLibraryPreview(selectedId);
        if (!previewData || state.selectedLibraryId !== selectedId) return;
        const idx = (state.libraries || []).findIndex((lib) => lib.id === selectedId);
        if (idx < 0) return;
        const updated = {
            ...state.libraries[idx],
            tile_count: Number(previewData.count || state.libraries[idx].tile_count || 0),
            previews: Array.isArray(previewData.images) && previewData.images.length
                ? previewData.images
                : (state.libraries[idx].previews || []),
        };
        state.libraries[idx] = updated;
        if (typeof UI.updateLibraryStripItem === 'function') {
            UI.updateLibraryStripItem(updated);
        }
        if (typeof UI.setLibraryPreviewLoadState === 'function') {
            UI.setLibraryPreviewLoadState({
                tileCount: Number(updated.tile_count || 0),
                images: updated.previews || [],
                loading: false,
                error: '',
            });
        }
        document.dispatchEvent(new CustomEvent('library:selected-updated', { detail: { library: updated } }));
    } catch (err) {
        if (typeof UI.setLibraryPreviewLoadState === 'function') {
            UI.setLibraryPreviewLoadState({
                tileCount: currentTileCount,
                images: currentPreviews,
                loading: false,
                error: 'Could not load library thumbnails. Click again to retry.',
            });
        }
        showToast('Library preview unavailable', 'Could not load library thumbnails. Click again to retry.', 3200);
    }
}

// --- MAIN LIBRARY REFRESH ---
export async function refreshLibraries() {
    _bindLibraryEvents();
    const fetched = await API.getLibraries();
    state.libraries = _ensureSampleLibraryFallback(fetched, state.isAnonymous);
    if (state.selectedLibraryId && !(state.libraries || []).some((lib) => lib.id === state.selectedLibraryId)) {
        state.selectedLibraryId = null;
        resetLibraryPreviewState();
        setLibraryPreviewVisibility(false);
        _emitCreateDraftDirty('library_selection_cleared');
    }
    UI.renderLibraryGrid(state.libraries, handleLibSelect, handleLibDelete, handleLibRename, handleLibPrivacy);
    UI.updateLibraryPreviewActions(handleLibDelete, handleLibRename, handleLibPrivacy);
    setLibraryWorkspaceVisibility(state.libraries, state.isAnonymous);
    _syncLibraryPreviewPanel();
    _applyLibraryDropzoneState();
    refreshSettingsVisibility();
    _applyLibraryDropzoneState();
}

// --- HANDLERS ---

export async function handleLibSelect(id, newName=null, newIsPublic=null) {
    // 1. Handle Creation (if newName provided)
    if (newName) {
        const isPublic = (newIsPublic === null || newIsPublic === undefined) ? false : !!newIsPublic;
        let res;
        try {
            res = await API.createLibrary(newName, isPublic);
        } catch (err) {
            showToast('Create library failed', String(err.message || err), { duration: 0, priority: 'error' });
            return;
        }
        const created = {
            id: res.id,
            name: res.name || newName,
            is_public: Boolean(res.is_public),
            is_owner: true,
            tile_count: 0,
            previews: [],
        };
        state.libraries = [
            created,
            ...(state.libraries || []).filter((lib) => lib.id !== created.id),
        ];
        document.dispatchEvent(new CustomEvent('library:created', { detail: { library: created } }));
        id = created.id;
        UI.renderLibraryGrid(state.libraries, handleLibSelect, handleLibDelete, handleLibRename, handleLibPrivacy);
        setLibraryWorkspaceVisibility(state.libraries, state.isAnonymous);
        API.logUiFlow('library', 'create', 'ok', {
            library_id: created.id,
            is_public: Boolean(created.is_public),
        }).catch(() => {});
    }

    if (!id) return;
    state.selectedLibraryId = id;
    resetLibraryPreviewState();

    // 2. Highlight selected library without rerendering the entire strip
    if (typeof UI.setLibraryStripSelection === 'function') {
        UI.setLibraryStripSelection(id);
    } else {
        UI.renderLibraryGrid(state.libraries, handleLibSelect, handleLibDelete, handleLibRename, handleLibPrivacy);
    }
    UI.updateLibraryPreviewActions(handleLibDelete, handleLibRename, handleLibPrivacy);
    setLibraryWorkspaceVisibility(state.libraries, state.isAnonymous);
    _applyLibraryDropzoneState();
    API.logUiFlow('library', 'select', 'ok', {
        library_id: id,
    }).catch(() => {});
    const selected = (state.libraries || []).find((lib) => String(lib.id || '') === String(id || '')) || null;
    document.dispatchEvent(new CustomEvent('library:selected-updated', { detail: { library: selected } }));

    // 3. Keep explore panel hidden for empty libraries; reveal collapsed once tiles exist.
    _syncLibraryPreviewPanel({ collapseOnReveal: true });
    UI.updateLibraryPreviewActions(handleLibDelete, handleLibRename, handleLibPrivacy);

    // 4. Setup Infinite Scroll
    attachLibraryPreviewScroll();
    refreshSettingsVisibility();
    _applyLibraryDropzoneState();
    _emitCreateDraftDirty('library_selected');
}

export async function handleLibPrivacy(id, isPublic) {
    try {
        await API.setLibraryPrivacy(id, isPublic);
        const idx = (state.libraries || []).findIndex((lib) => lib.id === id);
        if (idx >= 0) {
            const updated = { ...state.libraries[idx], is_public: Boolean(isPublic) };
            state.libraries[idx] = updated;
            if (typeof UI.updateLibraryStripItem === 'function') {
                UI.updateLibraryStripItem(updated);
            }
        }
        _applyLibraryDropzoneState();
    } catch (err) {
        showToast('Privacy update failed', String(err.message || err), { duration: 0, priority: 'error' });
    }
}

export async function handleLibRename(id, newName) {
    try {
        await API.renameLibrary(id, newName);
        const idx = (state.libraries || []).findIndex((lib) => lib.id === id);
        if (idx >= 0) {
            const updated = { ...state.libraries[idx], name: newName };
            state.libraries[idx] = updated;
            if (typeof UI.updateLibraryStripItem === 'function') {
                UI.updateLibraryStripItem(updated);
            }
            if (state.selectedLibraryId === id) {
                const nameEl = document.getElementById('libPreviewLibraryName');
                if (nameEl) nameEl.textContent = newName;
            }
        }
    } catch (err) {
        showToast('Rename failed', String(err.message || err), { duration: 0, priority: 'error' });
    }
}

export async function handleLibDelete(id) {
    const lib = (state.libraries || []).find((item) => item.id === id);
    const isPublicSample = Boolean(lib && lib.is_public && !lib.is_owner);
    const ok = await openConfirmModal({
        title: isPublicSample ? 'Remove library?' : 'Delete library?',
        message: isPublicSample ? UI_COPY.library.confirmDeletePublic : UI_COPY.library.confirmDelete,
        confirmText: isPublicSample ? 'Remove' : 'Delete',
        danger: true,
    });
    if (!ok) return;
    try {
        await API.deleteLibrary(id);
        state.libraries = (state.libraries || []).filter((lib) => lib.id !== id);
        state.libraries = _ensureSampleLibraryFallback(state.libraries, state.isAnonymous);
        if(state.selectedLibraryId === id) {
            state.selectedLibraryId = null;
            resetLibraryPreviewState();
            setLibraryPreviewVisibility(false);
            _emitCreateDraftDirty('library_deleted_selected');
        }
        UI.renderLibraryGrid(state.libraries, handleLibSelect, handleLibDelete, handleLibRename, handleLibPrivacy);
        UI.updateLibraryPreviewActions(handleLibDelete, handleLibRename, handleLibPrivacy);
        setLibraryWorkspaceVisibility(state.libraries, state.isAnonymous);
        _applyLibraryDropzoneState();
        refreshSettingsVisibility();
        _applyLibraryDropzoneState();
        _emitCreateDraftDirty('library_deleted');
        API.logUiFlow('library', 'delete', 'ok', {
            library_id: id,
        }).catch(() => {});
    } catch (err) {
        showToast('Delete failed', UI_COPY.library.deleteFailed(err.message || String(err)), { duration: 0, priority: 'error' });
        API.logUiFlow('library', 'delete', 'error', {
            library_id: id,
        }).catch(() => {});
    }
}
